/**
 * HR → «Зарплаты» (26.08, владелец — переписан заново). Вкусы дома: всё на одном
 * экране, мелкие шрифты, человеческий язык, деньги золотом. Период — зарплатная
 * неделя пт–чт со стрелками (или месяц). Строка оператора: имя · игр · ₽итог;
 * тап — раскрыв: разбивка по видам (только ненулевые) и дни с составом.
 * 04.09 (владелец): подтверждённые покупки операторов — компенсация в составе
 * выплаты, строка дня и часть «🧾 Покупки» лаймом (var(--lime)).
 * 05.09 (владелец): премии — часть «🎁 Премии» и строки дня синим (var(--blue));
 * штрафы (раньше HR их не рисовал, хотя total уже с вычетом) — часть
 * «⚠️ Штрафы −₽N» и строки дня красным (var(--red)).
 * 13.10 (архив 1С, ПР3): игры 1С и всё до 01.03.2026 выплачены через 1С — строки с подписью и отдельная сумма
 * «Начислено (выплачено через 1С)»; в «Всего к выплате» и расчётки не входят. Такси отчёта 1С — «доп 1С».
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../../api/client";
import type { DateRange } from "../../components/DateSelector";
import { todayBusiness, toBusinessDate, addDays, fmtDateApi } from "../../utils/date";
import css from "./board.module.css";
import { PayslipsPanel } from "./PayslipsPanel";
import { PayslipPending } from "./PayslipPending";
import { useAuth } from "../../auth";
import { EwPayNote } from "./EwPayNote";
import type { EwPayInfo } from "./ewPayText";
import { fetchHolds, holdTone, rub, signedRub } from "../../hooks/payoutsApi";
import hold from "./hold.module.css";
import { PAID_1C_LABEL, PAID_1C_SHORT, TAXI_1C_LABEL } from "@ub/shared/archive1c";
import { SlipMarkTag, slipRowClass } from "../../components/shared/SlipMark";

interface SalQuest { quest_name?: string; short_name?: string; time?: string; rate?: number; zero_rate?: boolean; status?: string; taxi_amount?: number; paid_1c?: boolean; taxi_1c?: boolean }
interface SalMc { mc_name?: string; quest_short?: string; time?: string; rate?: number; paid_1c?: boolean }
interface SalHa { branch?: string; branch_id?: string; hours?: number; rate?: number; earning?: number; time_from?: string; time_to?: string; reported?: boolean }
/* 15.10 (владелец): + разбивка оплаты (EwPayInfo) — пояснение второй строкой под допработой */
interface SalEw extends EwPayInfo { time_from?: string; time_to?: string; description?: string; earning?: number; taxi?: number }
interface SalPurchase { id?: number; amount?: number; description?: string; comment?: string | null }
/* 05.09: премии (плюс) и штрафы (минус) — сервер отдаёт только status='sent' */
interface SalBonus { id?: number; amount?: number; reason?: string; mark?: string | null }   // 26.10: mark — отметка в расчётке
interface SalPenalty { id?: string | number; amount?: number; reason?: string; mark?: string | null }
interface SalDay { quests?: SalQuest[]; mc?: SalMc[]; hall_admin?: SalHa | null; extra_work?: SalEw[]; purchases?: SalPurchase[]; bonuses?: SalBonus[]; penalties?: SalPenalty[]; day_total?: number }
interface Sal {
  staffId: string; name: string; total: number; games_count: number;
  quest_earnings: number; mc_earnings: number; hall_admin_earnings: number;
  extra_work_earnings: number; taxi_earnings: number; purchase_total?: number; bonus_total?: number; penalty_total?: number; days: Record<string, SalDay>;
  /** ПР3 архива 1С: начислено и выплачено через 1С — НЕ в total. */
  paid_1c_total?: number;
}

const fmtR = (n: number) => `₽${String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}`;
const dm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
const t5 = (t?: string) => (t ?? "").slice(0, 5);

export function SalaryTab({ range }: { range: DateRange }) {
  const [rows, setRows] = useState<Sal[]>([]);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);   // 10.10: пересчёт после решений в окне проверки допработ
  const [open, setOpen] = useState<string | null>(null);
  /* 16.10 (владелец): колонка «Холд» — баланс на сегодня (заработано за закрытые недели − выдано), не за период */
  const [holds, setHolds] = useState<Map<string, number> | null>(null);
  const [holdNames, setHoldNames] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let alive = true;
    fetchHolds().then((h) => {
      if (!alive) return;
      setHolds(new Map(h.rows.map((r) => [r.staff_id, r.hold]))); setHoldNames(new Map(h.rows.map((r) => [r.staff_id, r.name])));
    }, () => { if (alive) setHolds(null); });
    return () => { alive = false; };
  }, [tick]);
  /* период — ШАПОЧНЫЙ календарь экрана (26.08 v2, владелец: «в углу уже есть —
     используем его»); пока не выбран — текущая зарплатная неделя пт–чт */
  const period = useMemo(() => {
    if (range?.start) {
      const from = fmtDateApi(toBusinessDate(range.start));
      return { from, to: range.end ? fmtDateApi(toBusinessDate(range.end)) : from };
    }
    const t = todayBusiness();
    const start = addDays(t, -((t.getUTCDay() - 5 + 7) % 7));
    return { from: fmtDateApi(start), to: fmtDateApi(addDays(start, 6)) };
  }, [range]);

  const shownPeriod = useRef("");   // 10.10: пересчёт после окна допработ — без «Считаю…», иначе готовые расчётки пропадали
  useEffect(() => {
    let alive = true;
    if (shownPeriod.current !== `${period.from}|${period.to}`) { setLoading(true); shownPeriod.current = `${period.from}|${period.to}`; }
    api<{ results?: Sal[] }>(`/api/finance/salary?from=${period.from}&to=${period.to}`)
      .then((r) => { if (alive) { setRows(r.results ?? []); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period, tick]);

  const paid = rows.filter((s) => (s.total || 0) !== 0);
  const grand = paid.reduce((a, s) => a + (s.total || 0), 0);
  /* ПР3 архива 1С: тот, у кого за период только выплаченное через 1С, виден в списке, но не в расчётках */
  const shown = rows.filter((s) => (s.total || 0) !== 0 || (s.paid_1c_total || 0) > 0);
  const grand1c = shown.reduce((a, s) => a + (s.paid_1c_total || 0), 0);

  return (
    <div className={css.content}>
      <div className={css.salGrand}>Всего к выплате: <b>{fmtR(grand)}</b> · {paid.length} чел.</div>
      {grand1c > 0 ? <div className={css.salPaid1c}>{PAID_1C_LABEL}: <b>{fmtR(grand1c)}</b> — в «Всего к выплате» не входит</div> : null}
      {holds ? <HoldLine holds={holds} /> : null}
      {holds && !loading ? <HoldOnly holds={holds} names={holdNames} paid={shown.map((s) => s.staffId)} /> : null}
      {/* 02.09 (владелец): расчётки из ЭТИХ цифр — сформировать, посмотреть, разослать */}
      {/* 10.10 (владелец: «некоторых доп работ нет в расчётках»): что ждёт решения ОО — до формирования расчёток */}
      <PayslipPending period={period} onChanged={() => setTick((n) => n + 1)} />
      {!loading && paid.length ? <PayslipsPanel rows={paid} period={period} issuedBy={user?.name ?? "ОО"} /> : null}

      {!loading && shown.length ? (
        <div className={hold.salHead}><span className={hold.salHeadName}>Сотрудник</span><span>Игр</span><span className={hold.salHeadNum}>За период</span>{holds ? <span className={hold.salHold}>Холд</span> : null}</div>
      ) : null}
      {loading ? <div className={css.empty}>Считаю…</div> : shown.length === 0 ? <div className={css.empty}>Выплат за период нет</div> : shown.map((s) => {
        const days = Object.entries(s.days ?? {}).sort(([a], [b]) => a.localeCompare(b));
        const parts: [string, number, string?][] = [
          ["🎭 Игры", s.quest_earnings], ["🎓 МК", s.mc_earnings, css.salPartMc], ["◆ Админ зала", s.hall_admin_earnings],
          ["🔧 Допработы", s.extra_work_earnings], ["🚕 Такси", s.taxi_earnings], ["🧾 Покупки", s.purchase_total ?? 0, css.salPartBuy],
          /* 05.09: премии плюсом синим, штрафы минусом красным (отрицательное v → «−₽N») */
          ["🎁 Премии", s.bonus_total ?? 0, css.salPartBonus], ["⚠️ Штрафы", -(s.penalty_total ?? 0), css.salPartFine],
        ];
        return (
          <div key={s.staffId}>
            <div className={css.salRow} role="button" onClick={() => setOpen((o) => (o === s.staffId ? null : s.staffId))}>
              <span className={css.salName}>{s.name}</span>
              <span className={css.salGames}>{s.games_count} игр</span>
              <span className={css.salTotal}>{fmtR(s.total)}</span>
              {holds ? <span className={`${hold.salHold} ${hold[holdTone(holds.get(s.staffId) ?? 0)] ?? ""}`}>{signedRub(holds.get(s.staffId) ?? 0)}</span> : null}
            </div>
            {open === s.staffId ? (
              <div className={css.salExpand}>
                <div className={css.salParts}>
                  {parts.filter(([, v]) => (v || 0) !== 0).map(([l, v, cls]) => <span key={l} className={`${css.salPart} ${cls ?? ""}`}>{l} <b>{v < 0 ? `−${fmtR(-v)}` : fmtR(v)}</b></span>)}
                </div>
                {(s.paid_1c_total ?? 0) > 0 ? <div className={css.salPaid1c}>{PAID_1C_LABEL}: <b>{fmtR(s.paid_1c_total ?? 0)}</b> — не входит в итог</div> : null}
                {days.map(([d, day]) => (
                  <div key={d} className={css.salDay}>
                    <div className={css.salDayHead}>{dm(d)} <span className={css.salDaySum}>{fmtR(day.day_total ?? 0)}</span></div>
                    {(day.quests ?? []).map((q, i) => <div key={`q${i}`} className={`${css.salLine} ${css.salLnGame}`}>🎭 {t5(q.time)} {q.short_name || q.quest_name}{q.status === "cancelled" ? " · отмена" : q.zero_rate ? " · без ставки" : ""}{q.paid_1c ? ` · ${PAID_1C_SHORT}` : ""}{(q.taxi_amount ?? 0) > 0 ? ` · ${q.taxi_1c ? TAXI_1C_LABEL : "🚕"} ${fmtR(q.taxi_amount ?? 0)}` : ""} <b>{fmtR(q.rate ?? 0)}</b></div>)}
                    {(day.mc ?? []).map((mc, i) => <div key={`m${i}`} className={`${css.salLine} ${css.salLnMc}`}>🎓 {t5(mc.time)} {mc.mc_name}{mc.quest_short ? ` · ${mc.quest_short}` : ""}{mc.paid_1c ? ` · ${PAID_1C_SHORT}` : ""} <b>{fmtR(mc.rate ?? 0)}</b></div>)}
                    {day.hall_admin ? <div className={`${css.salLine} ${css.salLnHa}`}>◆ {(day.hall_admin.branch_id || day.hall_admin.branch || "").toUpperCase()} {t5(day.hall_admin.time_from)}–{t5(day.hall_admin.time_to)} ({(day.hall_admin as { fixed?: boolean }).fixed ? `${day.hall_admin.hours} ч · сумма задана вручную` : `${day.hall_admin.hours} ч × ₽${day.hall_admin.rate}/ч`}){day.hall_admin.reported ? "" : " · без отчёта"} <b>{fmtR(day.hall_admin.earning ?? 0)}</b></div> : null}
                    {(day.extra_work ?? []).map((e, i) => <div key={`e${i}`} className={`${css.salLine} ${css.salLnEw}`}>🔧 {t5(e.time_from)}–{t5(e.time_to)}{e.description ? ` ${e.description}` : ""}{e.taxi ? ` · 🚕 ${fmtR(e.taxi)}` : ""} <b>{fmtR(e.earning ?? 0)}</b><EwPayNote e={e} /></div>)}
                    {(day.purchases ?? []).map((b, i) => <div key={`b${i}`} className={`${css.salLine} ${css.salLnBuy}`}>🧾 Покупка · компенсация{b.description ? ` · ${b.description}` : ""}{b.comment ? ` — ${b.comment}` : ""} <b>{fmtR(b.amount ?? 0)}</b></div>)}
                    {(day.bonuses ?? []).map((n, i) => <div key={`n${i}`} className={`${css.salLine} ${css.salLnBonus} ${slipRowClass(n.mark, "bonus")}`}>🎁 Премия<SlipMarkTag mark={n.mark} tone="bonus" />{n.reason ? ` · ${n.reason}` : ""} <b>{fmtR(n.amount ?? 0)}</b></div>)}
                    {(day.penalties ?? []).map((p, i) => <div key={`p${i}`} className={`${css.salLine} ${css.salLnFine} ${slipRowClass(p.mark, "pen")}`}>⚠️ Штраф<SlipMarkTag mark={p.mark} tone="pen" />{p.reason ? ` · ${p.reason}` : ""} <b>−{fmtR(Math.abs(p.amount ?? 0))}</b></div>)}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** «Холд сейчас: мы должны ₽A · нам должны ₽B» — строка итогов под «Всего к выплате» (16.10). */
function HoldLine({ holds }: { holds: Map<string, number> }) {
  const v = [...holds.values()];
  const owe = v.filter((x) => x > 0).reduce((a, x) => a + x, 0), debt = v.filter((x) => x < 0).reduce((a, x) => a - x, 0);
  return <div className={hold.salHoldLine}>Холд сейчас: мы должны <b className={hold.owe}>{rub(owe)}</b> · нам должны <b className={hold.debt}>{rub(debt)}</b></div>;
}

/** Холд есть, а начислений за период нет (не работал, уволен) — строкой, чтобы долг не терялся из вида (ревью 16.10). */
function HoldOnly({ holds, names, paid }: { holds: Map<string, number>; names: Map<string, string>; paid: string[] }) {
  const inList = new Set(paid);
  const rest = [...holds].filter(([id, v]) => v !== 0 && !inList.has(id));
  if (!rest.length) return null;
  return <div className={hold.salHoldLine}>Холд без начислений за период: {rest.map(([id, v], i) => <span key={id}>{i ? ", " : ""}{names.get(id) ?? id} <b className={hold[holdTone(v)] ?? ""}>{signedRub(v)}</b></span>)}</div>;
}
